import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Play, RotateCcw, Search, AlertTriangle, Link2, FileSearch, ChevronRight } from "lucide-react";
import { koppelOverzicht, koppelProducten, koppelAnalyse, koppelRun, koppelUitvoeren, koppelHerstel, slidesList, koppelHandmatig } from "@/lib/adminApi";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { VORM_OPTIES } from "@/lib/vormen";

// Koppelingen-tab: per CNK zien welke stof/vorm/profiel gekoppeld is, droge-run
// analyses draaien en na goedkeuring uitvoeren (met hersteloptie).
const INPUT_CLS =
  "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#0a5a3c]";
const PER_PAGE = 25;

const STATUS_STYLE = {
  correct: "bg-emerald-100 text-emerald-800",
  koppelbaar: "bg-blue-100 text-blue-800",
  onzeker: "bg-amber-100 text-amber-800",
  ontbrekend: "bg-slate-100 text-slate-500",
};
const STATUS_LABEL = {
  correct: "correct", koppelbaar: "koppelbaar", onzeker: "onzeker", ontbrekend: "ontbrekend",
};

const Badge = ({ status }) => (
  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[status]}`} data-testid={`status-${status}`}>
    {STATUS_LABEL[status]}
  </span>
);

const KoppelingenTab = () => {
  const [overzicht, setOverzicht] = useState(null);
  const [report, setReport] = useState(null);      // volledige run (concept of uitgevoerd)
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");
  const [loadingRows, setLoadingRows] = useState(true);
  // handmatig koppelen per CNK: kies stof + farmaceutische vorm
  const [koppelDoel, setKoppelDoel] = useState(null);   // rij die gekoppeld wordt
  const [stoffen, setStoffen] = useState([]);
  const [profielen, setProfielen] = useState([]);
  const [stofKeuze, setStofKeuze] = useState(null);     // stof-id
  const [vormKeuze, setVormKeuze] = useState("");       // "" = basisprofiel
  const [vormCustom, setVormCustom] = useState("");
  const [savingKoppel, setSavingKoppel] = useState(false);

  const loadOverzicht = useCallback(async () => {
    try {
      setOverzicht(await koppelOverzicht());
    } catch {
      toast.error("Kon overzicht niet laden");
    }
  }, []);

  const loadRows = useCallback(async () => {
    setLoadingRows(true);
    try {
      const d = await koppelProducten({ status: statusFilter, q, page, per_page: PER_PAGE });
      setRows(d.rows || []);
      setTotal(d.total ?? 0);
    } catch {
      toast.error("Kon producten niet laden");
    }
    setLoadingRows(false);
  }, [statusFilter, q, page]);

  useEffect(() => { loadOverzicht(); }, [loadOverzicht]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const openRun = async (runId) => {
    try {
      setReport(await koppelRun(runId));
    } catch {
      toast.error("Kon rapport niet laden");
    }
  };

  const analyse = async () => {
    setBusy(true);
    try {
      toast.info("Analyse bezig — dit kan ±30 seconden duren…");
      const r = await koppelAnalyse();
      setReport(await koppelRun(r.run_id));
      toast.success("Controlerapport klaar — nog NIETS is gewijzigd");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Analyse mislukt");
    }
    setBusy(false);
  };

  const uitvoeren = async () => {
    const s = report.summary;
    if (!window.confirm(
      `Controlerapport uitvoeren?\n\n- ${s.herkoppelingen} CNK's worden herkoppeld\n- ${s.nieuwe_profielen} nieuwe vormprofielen worden aangemaakt\n\nBestaande teksten blijven onaangeroerd. Je kan dit achteraf herstellen.`)) return;
    setBusy(true);
    try {
      const r = await koppelUitvoeren(report.run_id);
      toast.success(`Uitgevoerd: ${r.herkoppeld} CNK's herkoppeld, ${r.nieuwe_profielen} profielen aangemaakt`);
      setReport(await koppelRun(report.run_id));
      await loadOverzicht();
      await loadRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Uitvoeren mislukt");
    }
    setBusy(false);
  };

  const herstel = async (runId) => {
    if (!window.confirm("Deze run terugdraaien? Alle koppelingen van deze run worden hersteld en de aangemaakte profielen verwijderd.")) return;
    setBusy(true);
    try {
      const r = await koppelHerstel(runId);
      toast.success(`Hersteld: ${r.hersteld} koppelingen teruggezet`);
      setReport(null);
      await loadOverzicht();
      await loadRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Herstellen mislukt");
    }
    setBusy(false);
  };

  const pages = Math.max(1, Math.ceil(total / PER_PAGE));

  // --- handmatig koppelen: kies de farmaceutische vorm; het profiel van die
  // stof + vorm wordt automatisch gezocht of aangemaakt.
  const openKoppel = async (row) => {
    setKoppelDoel(row);
    setStofKeuze(null);
    setVormCustom("");
    // stel de gedetecteerde productvorm alvast voor als die in de lijst past
    const pv = (row.productvorm || "").toLowerCase();
    const match = VORM_OPTIES.find((v) => pv && (pv === v || pv.includes(v)));
    setVormKeuze(match || "");
    try {
      const [st, pr] = await Promise.all([
        slidesList("werkzame-stoffen", { per_page: 1000 }),
        slidesList("inhoudsprofielen", { per_page: 1000 }),
      ]);
      const stofLijst = (st.rows || []).sort((a, b) => (a.naam || "").localeCompare(b.naam || ""));
      setStoffen(stofLijst);
      setProfielen(pr.rows || []);
      const huidigeStof = stofLijst.find((s) => (s.naam || "").toLowerCase() === (row.stof || "").toLowerCase());
      if (huidigeStof) setStofKeuze(huidigeStof.id);
    } catch {
      toast.error("Kon werkzame stoffen niet laden");
    }
  };

  const effectieveVorm = vormKeuze === "__custom" ? vormCustom.trim().toLowerCase() : vormKeuze;
  const gekozenStof = stoffen.find((s) => s.id === stofKeuze);
  const doelProfielNaam = gekozenStof
    ? (effectieveVorm ? `${gekozenStof.naam} - ${effectieveVorm}` : `${gekozenStof.naam} - basisprofiel`)
    : null;
  const doelBestaat = !!(gekozenStof && profielen.some((p) =>
    p.werkzame_stof_id === gekozenStof.id &&
    (effectieveVorm ? (p.toedieningsvorm || "").toLowerCase() === effectieveVorm : !p.toedieningsvorm)));

  const saveKoppel = async () => {
    if (!stofKeuze) {
      toast.error("Kies eerst een werkzame stof");
      return;
    }
    if (vormKeuze === "__custom" && !vormCustom.trim()) {
      toast.error("Vul de vorm in");
      return;
    }
    setSavingKoppel(true);
    try {
      const r = await koppelHandmatig({
        product_id: koppelDoel.id, stof_id: stofKeuze, vorm: effectieveVorm,
      });
      toast.success(
        `CNK ${r.cnk} gekoppeld aan '${r.profiel_naam}'` +
        (r.aangemaakt ? " (profiel nieuw aangemaakt)" : "") + " — meteen actief in de slideshow");
      setKoppelDoel(null);
      await loadOverzicht();
      await loadRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Koppelen mislukt");
    }
    setSavingKoppel(false);
  };

  return (
    <div className="space-y-5" data-testid="koppelingen-tab">
      {/* statistieken */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="koppel-stats">
        {[
          ["Totaal CNK's", overzicht?.totaal, "text-[#0a2540]"],
          ["Correct", overzicht?.correct, "text-emerald-700"],
          ["Koppelbaar", overzicht?.koppelbaar, "text-blue-700"],
          ["Onzeker", overzicht?.onzeker, "text-amber-700"],
          ["Ontbrekend", overzicht?.ontbrekend, "text-slate-500"],
        ].map(([label, val, tone]) => (
          <div key={label} className="rounded-xl border border-slate-200 bg-white px-4 py-3">
            <p className={`text-2xl font-bold ${tone}`}>{val ?? "–"}</p>
            <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
          </div>
        ))}
      </div>

      {/* analyse-acties */}
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex-1 text-sm text-slate-600">
          <p className="font-medium text-[#0a2540]">Automatische koppeling (CNK → stof + toedieningsvorm)</p>
          <p className="mt-0.5 text-xs text-slate-400">
            Een analyse schrijft nooit iets weg: je krijgt eerst een controlerapport, en pas als jij
            bevestigt worden vormprofielen aangemaakt en CNK's herkoppeld. Alles is achteraf herstelbaar.
          </p>
        </div>
        <button onClick={analyse} disabled={busy} data-testid="koppel-analyse-btn"
          className="flex items-center gap-1.5 rounded-lg bg-[#0a2540] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#0a1f38] disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSearch className="h-4 w-4" />}
          Nieuwe analyse (droge run)
        </button>
      </div>

      {/* eerdere runs */}
      {overzicht?.runs?.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="koppel-runs">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Eerdere analyses</p>
          <div className="space-y-2">
            {overzicht.runs.map((r) => (
              <div key={r.run_id} className="flex items-center gap-3 text-sm">
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                  r.status === "uitgevoerd" ? "bg-emerald-100 text-emerald-800"
                  : r.status === "hersteld" ? "bg-slate-100 text-slate-500"
                  : "bg-blue-100 text-blue-800"}`}>{r.status}</span>
                <span className="text-xs text-slate-400">{new Date(r.created_at).toLocaleString("nl-BE")}</span>
                {r.summary && (
                  <span className="text-xs text-slate-500">
                    {r.summary.herkoppelingen} herkoppelingen · {r.summary.nieuwe_profielen} nieuwe profielen
                  </span>
                )}
                <button onClick={() => openRun(r.run_id)} data-testid={`koppel-open-${r.run_id}`}
                  className="ml-auto flex items-center gap-1 text-xs font-medium text-[#0a2540] hover:underline">
                  Rapport <ChevronRight className="h-3.5 w-3.5" />
                </button>
                {r.status === "uitgevoerd" && (
                  <button onClick={() => herstel(r.run_id)} data-testid={`koppel-herstel-${r.run_id}`}
                    className="flex items-center gap-1 rounded-lg border border-rose-200 px-2 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50">
                    <RotateCcw className="h-3 w-3" /> Herstel
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* controlerapport */}
      {report && (
        <div className="space-y-4 rounded-xl border border-blue-200 bg-blue-50/50 p-5" data-testid="koppel-report">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-semibold text-[#0a2540]">Controlerapport — status: {report.status}</p>
              <p className="mt-0.5 text-xs text-slate-500">{new Date(report.created_at).toLocaleString("nl-BE")}</p>
            </div>
            <div className="flex gap-2">
              {report.status === "concept" && (
                <button onClick={uitvoeren} disabled={busy} data-testid="koppel-uitvoeren-btn"
                  className="flex items-center gap-1.5 rounded-lg bg-[#0a5a3c] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#084a31] disabled:opacity-50">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                  Uitvoeren ({report.summary?.herkoppelingen} herkoppelingen)
                </button>
              )}
              {report.status === "uitgevoerd" && (
                <button onClick={() => herstel(report.run_id)} disabled={busy} data-testid="koppel-herstel-btn"
                  className="flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-2 text-sm font-medium text-rose-600 hover:bg-rose-50">
                  <RotateCcw className="h-4 w-4" /> Herstel deze run
                </button>
              )}
              <button onClick={() => setReport(null)} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">Sluit</button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {[["Herkoppelingen", report.summary?.herkoppelingen], ["Nieuwe vormprofielen", report.summary?.nieuwe_profielen],
              ["Bestaande profielen hergebruikt", report.summary?.bestaande_profielen_hergebruikt],
              ["Controle nodig", report.summary?.controle_nodig]].map(([l, v]) => (
              <div key={l} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-center">
                <p className="text-lg font-bold text-[#0a2540]">{v ?? 0}</p>
                <p className="text-[11px] uppercase tracking-wide text-slate-500">{l}</p>
              </div>
            ))}
          </div>
          {report.nieuwe_profielen?.length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Nieuwe profielen die worden aangemaakt ({report.nieuwe_profielen.length})
              </p>
              <div className="flex max-h-32 flex-wrap gap-1.5 overflow-y-auto">
                {report.nieuwe_profielen.map((p, i) => (
                  <span key={i} className="rounded-full bg-[#0a5a3c]/10 px-2.5 py-1 text-xs font-medium text-[#0a5a3c]">{p.profiel_naam}</span>
                ))}
              </div>
            </div>
          )}
          {report.voorstellen?.length > 0 && (
            <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200 bg-white">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-left uppercase tracking-wide text-slate-500">
                  <tr><th className="px-3 py-2">CNK</th><th className="px-3 py-2">Product</th><th className="px-3 py-2">Van</th><th className="px-3 py-2">Naar</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {report.voorstellen.slice(0, 50).map((v) => (
                    <tr key={v.product_id}>
                      <td className="px-3 py-1.5 font-mono">{v.cnk}</td>
                      <td className="max-w-[220px] truncate px-3 py-1.5" title={v.product_naam}>{v.product_naam}</td>
                      <td className="px-3 py-1.5 text-slate-500">{v.huidig_profiel}</td>
                      <td className="px-3 py-1.5 font-medium text-[#0a5a3c]">{v.naar_profiel}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {report.voorstellen.length > 50 && (
                <p className="px-3 py-2 text-[11px] text-slate-400">… en {report.voorstellen.length - 50} meer</p>
              )}
            </div>
          )}
          {report.review?.length > 0 && (
            <p className="flex items-center gap-2 text-xs text-amber-700">
              <AlertTriangle className="h-4 w-4" />
              {report.review.length} producten vereisen handmatige controle en worden NIET automatisch gekoppeld (zie filter "onzeker" hieronder).
            </p>
          )}
        </div>
      )}

      {/* per-CNK tabel */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1 sm:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Zoek op CNK, product of stof…" data-testid="koppel-search" className={`${INPUT_CLS} pl-9`} />
        </div>
        {["", "correct", "koppelbaar", "onzeker", "ontbrekend"].map((st) => (
          <button key={st} onClick={() => { setStatusFilter(st); setPage(1); }} data-testid={`koppel-filter-${st || "alle"}`}
            className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
              statusFilter === st ? "bg-[#0a2540] text-white" : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
            {st || "alle"}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="koppel-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">CNK</th><th className="px-4 py-3">Product</th>
              <th className="px-4 py-3">Werkzame stof</th><th className="px-4 py-3">Toedieningsvorm</th>
              <th className="px-4 py-3">Inhoudsprofiel</th><th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actie</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loadingRows ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">
                <Link2 className="mx-auto mb-2 h-5 w-5 text-slate-300" />Geen resultaten
              </td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} data-testid={`koppel-row-${r.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-2.5 font-mono text-xs">{r.cnk}</td>
                <td className="max-w-[260px] truncate px-4 py-2.5 font-medium text-[#0a2540]" title={r.naam}>{r.naam}</td>
                <td className="px-4 py-2.5 text-slate-600">{r.stof || <span className="text-slate-300">?</span>}</td>
                <td className="px-4 py-2.5 text-slate-600">{r.toedieningsvorm || <span className="text-slate-300">-</span>}</td>
                <td className="max-w-[220px] truncate px-4 py-2.5 text-slate-600" title={r.profiel || ""}>{r.profiel || "-"}</td>
                <td className="px-4 py-2.5">
                  <Badge status={r.status} />
                  {r.reden && <p className="mt-0.5 max-w-[220px] text-[11px] leading-tight text-amber-700">{r.reden}</p>}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <button onClick={() => openKoppel(r)} data-testid={`koppel-link-${r.id}`}
                    className="inline-flex items-center gap-1 rounded-lg border border-[#0a5a3c]/30 px-2.5 py-1.5 text-xs font-semibold text-[#0a5a3c] hover:bg-[#0a5a3c]/5">
                    <Link2 className="h-3.5 w-3.5" /> Koppel
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-sm text-slate-500" data-testid="koppel-pagination">
          <span data-testid="koppel-total">{total === 1 ? "1 resultaat" : `${total} resultaten`} · pagina {page}/{pages}</span>
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} data-testid="koppel-page-prev"
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40">Vorige</button>
            <button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages} data-testid="koppel-page-next"
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40">Volgende</button>
          </div>
        </div>
      </div>

      {/* dialog: handmatig koppelen via farmaceutische vorm */}
      <Dialog open={!!koppelDoel} onOpenChange={(o) => !o && setKoppelDoel(null)}>
        <DialogContent className="max-w-lg" data-testid="koppel-dialog">
          <DialogHeader>
            <DialogTitle>Koppel aan farmaceutische vorm</DialogTitle>
          </DialogHeader>
          {koppelDoel && (
            <div className="space-y-4">
              <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm">
                <p className="font-medium text-[#0a2540]">{koppelDoel.naam}</p>
                <p className="text-xs text-slate-500">CNK {koppelDoel.cnk}
                  {koppelDoel.productvorm ? ` · gedetecteerde vorm: ${koppelDoel.productvorm}` : ""}</p>
                {koppelDoel.reden && (
                  <p className="mt-1 flex items-center gap-1 text-[11px] text-amber-700">
                    <AlertTriangle className="h-3 w-3" /> {koppelDoel.reden}
                  </p>
                )}
              </div>

              {/* stap 1: werkzame stof */}
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  1. Werkzame stof
                </label>
                <select value={stofKeuze || ""} onChange={(e) => setStofKeuze(e.target.value ? Number(e.target.value) : null)}
                  data-testid="koppel-stof-select" className={INPUT_CLS}>
                  <option value="">— kies werkzame stof —</option>
                  {stoffen.map((s) => <option key={s.id} value={s.id}>{s.naam}</option>)}
                </select>
              </div>

              {/* stap 2: farmaceutische vorm */}
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  2. Farmaceutische vorm
                </label>
                <div className="flex max-h-44 flex-wrap gap-1.5 overflow-y-auto rounded-lg border border-slate-200 p-2" data-testid="koppel-vorm-lijst">
                  <button onClick={() => setVormKeuze("")} data-testid="koppel-vorm-basis"
                    className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                      vormKeuze === "" ? "bg-[#0a5a3c] text-white" : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
                    algemeen (basisprofiel)
                  </button>
                  {VORM_OPTIES.map((v) => (
                    <button key={v} onClick={() => setVormKeuze(v)} data-testid={`koppel-vorm-${v.replace(/\s+/g, "-")}`}
                      className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                        vormKeuze === v ? "bg-[#0a5a3c] text-white" : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
                      {v}
                      {(koppelDoel.productvorm || "").toLowerCase().includes(v) && vormKeuze !== v && (
                        <span className="ml-1 text-[10px] text-amber-600">(voorgesteld)</span>
                      )}
                    </button>
                  ))}
                  <button onClick={() => setVormKeuze("__custom")} data-testid="koppel-vorm-custom"
                    className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                      vormKeuze === "__custom" ? "bg-[#0a5a3c] text-white" : "border border-dashed border-slate-300 bg-white text-slate-500 hover:bg-slate-50"}`}>
                    andere…
                  </button>
                </div>
                {vormKeuze === "__custom" && (
                  <input value={vormCustom} onChange={(e) => setVormCustom(e.target.value)}
                    placeholder="Typ de exacte vorm, bv. klysma" data-testid="koppel-vorm-custom-input"
                    className={`${INPUT_CLS} mt-2`} />
                )}
              </div>

              {/* voorvertoning van het resultaat */}
              {doelProfielNaam && (
                <p className="rounded-lg bg-[#0a5a3c]/5 px-3 py-2 text-xs text-slate-600" data-testid="koppel-preview">
                  Koppelt aan profiel <strong className="text-[#0a2540]">“{doelProfielNaam}”</strong>{" "}
                  {doelBestaat
                    ? <span className="text-[#0a5a3c]">(bestaat al)</span>
                    : <span className="text-amber-700">(wordt nieuw aangemaakt)</span>}
                  {" "}— de medische teksten komen uit de werkzame stof{effectieveVorm ? " + vorm" : ""}, niets wordt gedupliceerd.
                </p>
              )}

              <div className="flex justify-end gap-2">
                <button onClick={() => setKoppelDoel(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
                  Annuleren
                </button>
                <button onClick={saveKoppel} disabled={savingKoppel || !stofKeuze} data-testid="koppel-dialog-save"
                  className="flex items-center gap-1.5 rounded-lg bg-[#0a5a3c] px-4 py-2 text-sm font-semibold text-white hover:bg-[#084a31] disabled:opacity-50">
                  {savingKoppel && <Loader2 className="h-4 w-4 animate-spin" />}
                  Koppelen
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KoppelingenTab;
